import { apiService } from './api.service.js'
import { cacheService } from './cache.service.js'
import { AUTH_CONSTANTS } from '../constants/auth.constants.js'

/**
 * Serviço de cache para contas com cache-first e respostas enriquecidas
 * Implementa padrão cache-first com TTL de 10 minutos,
 * deduplicação de requisições simultâneas, processamento de
 * respostas enriquecidas do backend (derivados.contas, derivados.patrimonioTotal)
 * e fallback offline.
 *
 * Chave de cache: contas
 * Eventos disparados: contas-cache-updated
 */
export class ContaCacheService {
  constructor() {
    this.cacheKey = 'contas'
    this.endpoint = 'contas'
    /** Promise em andamento para deduplicação de requisições simultâneas */
    this.loadingPromise = null
  }

  /**
   * Lista contas com cache-first (TTL de 10 minutos)
   * Usa cacheService.isValid() para verificação centralizada de TTL
   * Implementa deduplicação via loadingPromise
   * Implementa fallback offline retornando cache expirado quando sem conexão
   *
   * @returns {Promise<Array>} Lista de contas ativas
   */
  async listar() {
    // Cache-first: verifica validade com TTL padrão de 10 minutos
    if (cacheService.isValid(this.cacheKey)) {
      return cacheService.get(this.cacheKey) || []
    }

    // Fallback offline: retorna cache expirado quando sem conexão
    if (!navigator.onLine) {
      const cacheExpirado = cacheService.get(this.cacheKey)
      return cacheExpirado || []
    }

    // Deduplicação: reutiliza Promise em andamento
    if (this.loadingPromise) {
      return this.loadingPromise
    }

    // Cache miss: busca do backend
    this.loadingPromise = this._loadFromBackend()

    try {
      const result = await this.loadingPromise
      return result
    } finally {
      this.loadingPromise = null
    }
  }

  /**
   * Carrega contas do backend via GET.
   * Backend retorna: { success: true, data: [...] } (retrocompatibilidade).
   * Em caso de timeout de autenticação (AUTH_TIMEOUT), retorna dados do cache local
   * ou array vazio — sem propagar exceção e sem executar requisição à API.
   *
   * @returns {Promise<Array>} Lista de contas
   * @private
   */
  async _loadFromBackend () {
    try {
      const response = await apiService.get(this.endpoint)
      const contas = response.data?.data || response.data || response

      cacheService.set(this.cacheKey, contas)
      return contas || []
    } catch (error) {
      // Em timeout de auth, retorna cache local ou array vazio (Req 5.2)
      if (error.code === AUTH_CONSTANTS.AUTH_TIMEOUT_ERROR) {
        const cacheLocal = cacheService.get(this.cacheKey)
        return cacheLocal || []
      }
      throw error
    }
  }

  /**
   * Cria nova conta no backend e atualiza o cache local
   * Processa resposta enriquecida: { success: true, data: { entidade, derivados, avisos } }
   * derivados.contas substitui integralmente o cache de contas
   * derivados.patrimonioTotal atualiza o saldo mensal do mês corrente
   * Dispara evento contas-cache-updated
   *
   * @param {Object} dadosConta - Dados da conta a ser criada
   * @returns {Promise<Object>} Conta criada pelo backend
   */
  async criar(dadosConta) {
    const response = await apiService.post(this.endpoint, dadosConta)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData
    const derivados = responseData.derivados || {}

    // Processa derivados (contas, patrimonioTotal)
    this._processarDerivados(derivados)

    // Se derivados.contas não veio, adiciona ao cache manualmente
    if (!derivados.contas) {
      cacheService.addToArray(this.cacheKey, entidade)
    }

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('contas-cache-updated'))

    return entidade
  }

  /**
   * Atualiza conta existente no backend e sincroniza o cache local
   * Processa resposta enriquecida e dispara evento contas-cache-updated
   *
   * @param {string} id - ID da conta a ser atualizada
   * @param {Object} dadosAtualizacao - Dados atualizados da conta
   * @returns {Promise<Object>} Conta atualizada pelo backend
   */
  async atualizar(id, dadosAtualizacao) {
    const response = await apiService.put(`${this.endpoint}/${id}`, dadosAtualizacao)
    const responseData = response.data || response

    // Extrai do envelope enriquecido { entidade, derivados, avisos }
    const entidade = responseData.entidade || responseData
    const derivados = responseData.derivados || {}

    // Processa derivados (contas, patrimonioTotal)
    this._processarDerivados(derivados)

    // Se derivados.contas não veio, atualiza no cache manualmente
    if (!derivados.contas) {
      cacheService.updateInArray(this.cacheKey, id, entidade)
    }

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('contas-cache-updated'))

    return entidade
  }

  /**
   * Exclui conta do backend e remove do cache local
   * Processa resposta enriquecida e dispara evento contas-cache-updated
   *
   * @param {string} id - ID da conta a ser excluída
   * @returns {Promise<boolean>} True se excluída com sucesso
   */
  async excluir(id) {
    const response = await apiService.delete(`${this.endpoint}/${id}`)
    const responseData = response.data || response

    // Extrai derivados da resposta enriquecida
    const derivados = responseData.derivados || {}

    // Processa derivados (contas, patrimonioTotal)
    this._processarDerivados(derivados)

    // Se derivados.contas não veio, remove do cache manualmente
    if (!derivados.contas) {
      cacheService.removeFromArray(this.cacheKey, id)
    }

    // Dispara evento de atualização para UI
    window.dispatchEvent(new CustomEvent('contas-cache-updated'))

    return true
  }

  /**
   * Processa entidades derivadas da resposta enriquecida do backend
   * - derivados.contas: substitui integralmente o cache de contas (lista completa ordenada)
   * - derivados.patrimonioTotal: atualiza o patrimônio total no saldo mensal do mês corrente
   *
   * @param {Object} derivados - Objeto com entidades derivadas do backend
   * @param {Array} [derivados.contas] - Lista completa de contas ativas (substituição integral)
   * @param {number} [derivados.patrimonioTotal] - Patrimônio total recalculado
   * @private
   */
  _processarDerivados(derivados) {
    if (!derivados || Object.keys(derivados).length === 0) return

    // Substitui integralmente o cache de contas com a lista completa do backend
    if (derivados.contas) {
      cacheService.set(this.cacheKey, derivados.contas)
    }

    // Atualiza patrimônio total no saldo mensal do mês corrente
    if (derivados.patrimonioTotal !== undefined) {
      const hoje = new Date()
      const mes = String(hoje.getMonth() + 1).padStart(2, '0')
      const ano = String(hoje.getFullYear())
      const saldoKey = `saldo_mensal_${mes}_${ano}`
      const saldoAtual = cacheService.get(saldoKey)
      if (saldoAtual) {
        saldoAtual.patrimonioTotal = derivados.patrimonioTotal
        cacheService.set(saldoKey, saldoAtual)
        // Notifica dashboard e demais componentes que o patrimônio total foi atualizado
        window.dispatchEvent(new CustomEvent('saldo-mensal-cache-updated', {
          detail: { mes, ano }
        }))
      }
    }
  }

  /**
   * Busca conta por ID no cache local
   * @param {string} id - ID da conta
   * @returns {Object|null} Conta encontrada ou null
   */
  buscarPorId(id) {
    const contas = cacheService.get(this.cacheKey) || []
    return contas.find(conta => conta.id === id) || null
  }

  /**
   * Força recarregamento do cache de contas
   * Remove cache existente e busca dados atualizados do backend
   * @returns {Promise<Array>} Lista atualizada de contas
   */
  async recarregar() {
    cacheService.remove(this.cacheKey)
    return this.listar()
  }

  /**
   * Inicializa cache no startup do sistema
   * Só busca do backend se não tem cache válido
   * @returns {Promise<void>}
   */
  async inicializarCache() {
    try {
      if (!cacheService.isValid(this.cacheKey)) {
        await this._loadFromBackend()
      }
    } catch (error) {
      console.error('❌ Erro ao inicializar cache de contas:', error)
    }
  }

  /**
   * Limpa cache de contas
   */
  limparCache() {
    cacheService.remove(this.cacheKey)
  }
}

// Instância singleton
export const contaCacheService = new ContaCacheService()
